import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { PayoutAdjustment } from 'src/models/hq/franchise-fee-payout';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { formatCurrency } from 'src/utils/numberFormatter';
import * as Yup from 'yup';

interface FormValues {
  adjustmentType: 'credit' | 'debit';
  amount: string;
  reason: string;
}

interface AdjustmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    amount: number,
    reason: string,
    adjustmentId?: string
  ) => Promise<void>;
  adjustment?: PayoutAdjustment | null; // If provided, we're editing; otherwise we're creating
  currentAmountOwed: number;
}

const validationSchema = Yup.object().shape({
  adjustmentType: Yup.string().oneOf(['credit', 'debit']).required('Required'),
  amount: Yup.number()
    .typeError('Must be a number')
    .positive('Must be a positive number')
    .required('Required')
    .test(
      'maxDecimals',
      'Amount can only have up to 2 decimal places',
      (value) => {
        if (value === undefined || value === null) return true;
        // Check if the number has more than 2 decimal places
        const decimalPlaces = (value.toString().split('.')[1] || '').length;
        return decimalPlaces <= 2;
      }
    ),
  reason: Yup.string()
    .required('Required')
    .min(3, 'Reason must be at least 3 characters')
    .max(500, 'Reason cannot exceed 500 characters'),
});

export function AdjustmentDialog({
  open,
  onClose,
  onSubmit,
  adjustment,
  currentAmountOwed,
}: AdjustmentDialogProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFees.payouts',
  });

  const isEditing = !!adjustment;

  const initialValues: FormValues = {
    adjustmentType: adjustment
      ? adjustment.negative
        ? 'credit'
        : 'debit'
      : 'credit',
    amount: adjustment ? Math.abs(adjustment.amountInDollars).toString() : '',
    reason: adjustment?.reason || '',
  };

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting, resetForm }: FormikHelpers<FormValues>
  ) => {
    try {
      const amount = parseFloat(values.amount);
      if (isNaN(amount) || amount <= 0) return;

      // Convert to cents and apply sign based on adjustment type
      const amountInCents = Math.round(amount * 100);
      const finalAmount =
        values.adjustmentType === 'credit' ? -amountInCents : amountInCents;

      await onSubmit(finalAmount, values.reason, adjustment?.id.toString());
      resetForm();
      onClose();
    } catch (error) {
      // Error is handled by the toast in the mutation onError
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (resetForm: () => void) => {
    resetForm();
    onClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, isValid, dirty, submitForm, resetForm, isSubmitting }) => {
        const adjustmentAmount = parseFloat(values.amount) || 0;

        // Calculate new amount owed
        let newAmountOwed: number;
        if (isEditing && adjustment) {
          // For editing: remove the original adjustment, then add the new one
          const amountOwedWithoutAdjustment =
            currentAmountOwed - adjustment.amountInDollars;
          newAmountOwed =
            values.adjustmentType === 'credit'
              ? amountOwedWithoutAdjustment - adjustmentAmount
              : amountOwedWithoutAdjustment + adjustmentAmount;
        } else {
          // For creating: just add/subtract from current amount
          newAmountOwed =
            values.adjustmentType === 'credit'
              ? currentAmountOwed - adjustmentAmount
              : currentAmountOwed + adjustmentAmount;
        }

        return (
          <Dialog
            open={open}
            onClose={() => handleClose(resetForm)}
            title={isEditing ? t('editAdjustment') : t('addAdjustment')}
            actions={[
              {
                label: t('cancel'),
                onClick: () => handleClose(resetForm),
              },
              {
                label: isEditing ? t('saveChanges') : t('addAdjustment'),
                onClick: submitForm,
                disabled: !isValid || (!dirty && !isEditing) || isSubmitting,
                color: 'primary',
              },
            ]}
          >
            <Form>
              <Stack spacing={3}>
                <FormTextField
                  name="adjustmentType"
                  label={t('adjustmentType')}
                  fullWidth
                  options={[
                    { value: 'credit', label: t('credit') },
                    { value: 'debit', label: t('debit') },
                  ]}
                />

                <FormTextField
                  name="amount"
                  label={t('amount')}
                  type="number"
                  InputProps={{ inputProps: { step: '0.01', min: '0' } }}
                />

                <FormTextField
                  name="reason"
                  label={t('reason')}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder={t('adjustmentReasonPlaceholder')}
                />

                {/* Preview Section */}
                {adjustmentAmount > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {t('adjustmentPreview')}:
                    </Typography>

                    {isEditing && adjustment && (
                      <Typography variant="body2">
                        {t('originalAdjustment')}:{' '}
                        {adjustment.negative ? '-' : '+'}
                        {formatCurrency(Math.abs(adjustment.amountInDollars))}
                      </Typography>
                    )}

                    {!isEditing && (
                      <Typography variant="body2">
                        {t('currentAmountOwed')}:{' '}
                        {formatCurrency(currentAmountOwed)}
                      </Typography>
                    )}

                    <Typography variant="body2">
                      {isEditing
                        ? t('newAdjustment')
                        : values.adjustmentType === 'credit'
                        ? t('creditAdjustment')
                        : t('debitAdjustment')}
                      : {values.adjustmentType === 'credit' ? '-' : '+'}
                      {formatCurrency(adjustmentAmount)}
                    </Typography>

                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={newAmountOwed < 0 ? 'error.main' : 'text.primary'}
                    >
                      {t('newAmountOwed')}: {formatCurrency(newAmountOwed)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Form>
          </Dialog>
        );
      }}
    </Formik>
  );
}
